import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { Title, TitleProps } from "./Title";
import { SimpleRichText, SimpleRichTextProps } from "./SimpleRichText";
import { List, ListProps } from "./List";
import { sectionWrapperFull as sectionWrapper } from "./sectionWrappers";
import {
  AutomatedArticleRichtext,
  AutomatedArticleRichtextProps,
} from "./AutomatedArticleRichtext";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { ImageWithAspectRatioProps } from "./types";
import { EmbeddedHTML, EmbeddedHTMLProps } from "./EmbeddedHTML";

const contentContainer = tv({
  base: cn(
    "uipm:border-t uipm:border-[var(--typography-colors-text-faded)]",
    "uipm:pt-[var(--container-title-gap)]",
    // Mobile: single column
    "uipm:flex uipm:flex-col",
  ),
  variants: {
    layout: {
      right: cn(
        // Tablet and desktop: two equal columns
        "uipm:md:grid uipm:md:grid-cols-2",
        "uipm:md:gap-[var(--container-gutter)]",
      ),
      full: cn(
        // Full width: single column on all screen sizes
        "uipm:flex uipm:flex-col",
      ),
    },
  },
  defaultVariants: {
    layout: "right",
  },
});

const leftColumn = tv({
  base: cn(
    // Mobile: hidden (metadata will be shown inline if needed)
    "uipm:hidden",
    // Tablet and desktop: visible sticky sidebar
    "uipm:md:block",
    "uipm:md:sticky uipm:md:top-[var(--container-title-gap)]",
    "uipm:md:self-start",
  ),
});

const rightColumn = tv({
  base: cn(
    "uipm:flex uipm:flex-col",
    "uipm:w-full",
    // Mobile spacing between content elements
    "uipm:gap-8",
    // Tablet spacing between content elements
    "uipm:md:gap-12",
    // Desktop spacing between content elements
    "uipm:lg:gap-14",
  ),
});

const imageWrapper = tv({
  base: "uipm:w-full",
});

export interface ArticleSectionImageProps extends ImageWithAspectRatioProps {
  quality?: number;
}

export interface ArticleSectionContentItem {
  type:
    | "image"
    | "title"
    | "simpleRichText"
    | "list"
    | "automatedArticleRichtext"
    | "embeddedHTML";
  data?:
    | ArticleSectionImageProps
    | TitleProps
    | SimpleRichTextProps
    | ListProps
    | AutomatedArticleRichtextProps
    | EmbeddedHTMLProps;
}

export interface ArticleSectionProps
  extends Omit<ComponentProps<"section">, "title" | "content"> {
  backgroundColor?: "none" | "light" | "accent" | "accent-light";
  topPadding?: "none" | "default" | "large";
  bottomPadding?: "none" | "default";
  content?: ArticleSectionContentItem[];
  metadata?: {
    readTime?: string;
    publishedDate?: string;
  };
  layout?: "right" | "full";
}

export const ArticleSection: React.FC<ArticleSectionProps> = ({
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  content = [],
  metadata,
  layout = "right",
  id,
  ...props
}) => {
  const renderContentItem = (
    item: ArticleSectionContentItem,
    index: number,
  ) => {
    switch (item.type) {
      case "image": {
        const imageData = item.data as ArticleSectionImageProps;
        if (!imageData) return null;
        return (
          <div key={index} className={imageWrapper()}>
            <ImageWithAspectRatio
              {...imageData}
              borderRadius="var(--radius-sm)"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
            />
          </div>
        );
      }

      case "title": {
        const titleData = item.data as TitleProps;
        if (!titleData) return null;
        return <Title key={index} {...titleData} />;
      }

      case "simpleRichText": {
        const richTextData = item.data as SimpleRichTextProps;
        if (!richTextData) return null;
        return <SimpleRichText key={index} {...richTextData} />;
      }

      case "list": {
        const listData = item.data as ListProps;
        if (!listData) return null;
        return <List key={index} {...listData} />;
      }

      case "automatedArticleRichtext": {
        const richtextData = item.data as AutomatedArticleRichtextProps;
        if (!richtextData) return null;
        return <AutomatedArticleRichtext key={index} {...richtextData} />;
      }

      case "embeddedHTML": {
        const embeddedHTMLData = item.data as any;
        if (!embeddedHTMLData?.children) return null;
        // Return the children directly (which is already the EmbeddedHTML component)
        return (
          <React.Fragment key={index}>
            {embeddedHTMLData.children}
          </React.Fragment>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section
      id={id}
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
      {...props}
    >
      <div className="uipm:max-w-[1440px] uipm:mx-auto uipm:px-[var(--container-margin)]">
        <div className={contentContainer({ layout })}>
          {/* Left Column - Metadata Sidebar (visible on md+ screens, only for right layout) */}
          {layout === "right" && (
            <div className={leftColumn()}>
              {metadata && (
                <div className="uipm:flex uipm:flex-col uipm:gap-1">
                  {metadata.readTime && (
                    <div>
                      <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-bold">
                        Read:{" "}
                      </span>
                      <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-normal">
                        {metadata.readTime}
                      </span>
                    </div>
                  )}
                  {metadata.publishedDate && (
                    <div>
                      <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-bold">
                        Published:{" "}
                      </span>
                      <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-normal">
                        {metadata.publishedDate}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Right Column - Main Content Area */}
          <div className={rightColumn()}>
            {/* Mobile metadata (visible only on mobile, only for right layout) */}
            {layout === "right" && metadata && (
              <div className="uipm:flex uipm:flex-col uipm:gap-1 uipm:md:hidden">
                {metadata.readTime && (
                  <div>
                    <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-bold">
                      Read:{" "}
                    </span>
                    <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-normal">
                      {metadata.readTime}
                    </span>
                  </div>
                )}
                {metadata.publishedDate && (
                  <div>
                    <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-bold">
                      Published:{" "}
                    </span>
                    <span className="uipm:text-[#545050] uipm:text-[12px] uipm:leading-[18px] uipm:font-normal">
                      {metadata.publishedDate}
                    </span>
                  </div>
                )}
              </div>
            )}

            {content.map((item, index) => renderContentItem(item, index))}
          </div>
        </div>
      </div>
    </section>
  );
};

export type ArticleSectionVariants = VariantProps<typeof sectionWrapper>;
